import mixpanel from "mixpanel-browser"
import { authService } from "src/base/services"
import { isNilOrEmpty } from "./utils/common"
import { getMixpanelToken, getAppEnv } from "./utils/config-utility"

// Guards every export below: if init() failed (missing token, blocked script)
// we must not let a tracking call throw into the calling component.
let isReady = false

// The identity + tenant context that every event needs so reports can be
// filtered per tenant. Read fresh from storage on each use so a tenant switch
// is picked up without waiting for the next login.
const buildContext = () => {
  const user = authService.getUser() || {}
  const tenantInfo = authService.getTenantInfo() || {}

  return {
    env: getAppEnv(),
    user_id: authService.getUserId() || null,
    email: user.email || null,
    tenant_id: tenantInfo.id ?? null,
    tenant_name: tenantInfo.name || null,
  }
}

// Super properties are attached by Mixpanel to every event it sends, including
// the ones fired from call sites that pass no payload at all.
export const registerUserContext = () => {
  if (!isReady) return
  try {
    mixpanel.register(buildContext())
  } catch (error) {
    console.error('registerUserContext', error)
  }
}

const startMixpanel = () => {
  try {
    mixpanel.init(getMixpanelToken())
    isReady = true
    // A reload keeps the session but loses super properties that were never
    // persisted, so re-seed from localStorage instead of waiting for a login.
    if (authService.isAuthenticated()) registerUserContext()
  } catch (error) {
    console.error('startMixpanel', error)
  }
}

export const track = (name, data, callback) => {
  if (!isReady) return
  try {
    // Context is merged per event as well as registered, so events stay correct
    // even if register() has not run yet. Call-site data still wins.
    mixpanel.track(name, { ...buildContext(), ...data }, callback)
  } catch (error) {
    console.error('track', error)
    if (callback) callback()
  }
}

export const identifyUser = () => {
  if (!isReady) return
  try {
    const user = authService.getUser() || {}
    const userId = authService.getUserId()
    if (isNilOrEmpty(userId)) return

    mixpanel.identify(userId)

    mixpanel.people.set({
      name: [user.first_name, user.last_name].filter(Boolean).join(' '),
      $email: user.email,
    })

    registerUserContext()
  } catch (error) {
    console.error('identifyUser', error)
  }
}

// Clears super properties and issues a new distinct_id so the next person to
// log in on this browser is not merged into the previous user's profile.
export const resetUser = () => {
  if (!isReady) return
  try {
    mixpanel.reset()
  } catch (error) {
    console.error('resetUser', error)
  }
}

export default startMixpanel
